import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Calendar, Utensils, ShieldAlert, CheckCircle2, Sparkles } from 'lucide-react';
import { Restaurant } from '../types';
import { SafetyBadge } from '../components/SafetyBadge';
import { ViolationCard } from '../components/ViolationCard';
import { InspectionTimeline } from '../components/InspectionTimeline';
import { AskAI } from '../components/AskAI';
interface RestaurantDetailPageProps {
  restaurant: Restaurant;
  onBack: () => void;
}
export function RestaurantDetailPage({
  restaurant,
  onBack
}: RestaurantDetailPageProps) {
  const [aiOpen, setAiOpen] = useState(false);
  const latestInspection = restaurant.inspections[0];
  const hasViolations = latestInspection?.violations.length > 0;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="pb-24" data-id="element-196">
      {/* Header / Hero */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-10" data-id="element-197">
        <div className="max-w-4xl mx-auto px-4" data-id="element-198">
          <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-teal-700 font-medium mb-6 transition-colors" data-id="element-199">
            <ArrowLeft className="w-4 h-4" data-id="element-200" />
            Back to results
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-8" data-id="element-201">
            <div className="flex-1" data-id="element-202">
              <div className="flex items-center gap-3 mb-3" data-id="element-203">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium" data-id="element-204">
                  <Utensils className="w-3.5 h-3.5" data-id="element-205" />
                  {restaurant.cuisine}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-206">
                {restaurant.name}
              </h1>
              <div className="flex items-center gap-2 text-slate-600 mb-2" data-id="element-207">
                <MapPin className="w-5 h-5 text-slate-400 shrink-0" data-id="element-208" />
                <span className="text-lg" data-id="element-209">{restaurant.address}</span>
              </div>
            </div>

            <div className="shrink-0" data-id="element-210">
              <SafetyBadge grade={restaurant.currentGrade} size="large" className="w-40 shadow-sm" data-id="element-211" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-8 grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-212">
        {/* Main Content Column */}
        <div className="md:col-span-2 space-y-10" data-id="element-213">
          {/* Latest Inspection Summary */}
          <section data-id="element-214">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-215">
              <Calendar className="w-6 h-6 text-teal-600" data-id="element-216" />
              Latest Inspection
            </h2>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-217">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100" data-id="element-218">
                <div data-id="element-219">
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-1" data-id="element-220">
                    Date Inspected
                  </p>
                  <p className="text-lg font-bold text-slate-900" data-id="element-221">
                    {new Date(latestInspection.date).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  </p>
                </div>
                <div className="sm:text-right" data-id="element-222">
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-1" data-id="element-223">
                    Score
                  </p>
                  <p className="text-2xl font-black text-slate-900" data-id="element-224">
                    {latestInspection.score}
                    <span className="text-lg text-slate-400 font-medium" data-id="element-225">
                      /100
                    </span>
                  </p>
                </div>
              </div>

              {hasViolations ? <div data-id="element-226">
                  <div className="flex items-center gap-2 text-amber-700 mb-4" data-id="element-227">
                    <ShieldAlert className="w-5 h-5" data-id="element-228" />
                    <h3 className="font-bold text-lg" data-id="element-229">
                      Found {latestInspection.violations.length} Violations
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-230">
                    {latestInspection.violations.map(violation => <ViolationCard key={violation.id} violation={violation} data-id="element-231" />)}
                  </div>
                </div> : <div className="flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-xl" data-id="element-232">
                  <CheckCircle2 className="w-8 h-8 shrink-0" data-id="element-233" />
                  <div data-id="element-234">
                    <h3 className="font-bold text-lg" data-id="element-235">Perfect Inspection</h3>
                    <p className="text-green-800 text-sm" data-id="element-236">
                      No violations were found during this visit. The facility
                      met all health and safety standards.
                    </p>
                  </div>
                </div>}
            </div>
          </section>

          {/* Inspection History */}
          <section data-id="element-237">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-238">
              Inspection History
            </h2>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-239">
              <InspectionTimeline inspections={restaurant.inspections} data-id="element-240" />
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-241">
          <div className="bg-slate-100 rounded-2xl p-6" data-id="element-242">
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-243">About the Grades</h3>
            <ul className="space-y-3 text-sm text-slate-600" data-id="element-244">
              <li className="flex gap-2" data-id="element-245">
                <span className="font-bold text-green-700 w-4" data-id="element-246">A</span> 90-100
                points. Excellent compliance.
              </li>
              <li className="flex gap-2" data-id="element-247">
                <span className="font-bold text-blue-700 w-4" data-id="element-248">B</span> 80-89
                points. Good compliance, some issues.
              </li>
              <li className="flex gap-2" data-id="element-249">
                <span className="font-bold text-amber-700 w-4" data-id="element-250">C</span> 70-79
                points. Needs improvement.
              </li>
              <li className="flex gap-2" data-id="element-251">
                <span className="font-bold text-red-700 w-4" data-id="element-252">F</span> Below 70.
                Critical issues, possible closure.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Floating Ask AI Button */}
      <motion.button initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setAiOpen(true)} className={`fixed bottom-6 right-6 z-[60] bg-teal-600 text-white p-4 rounded-full shadow-xl shadow-teal-600/30 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 items-center gap-2 ${aiOpen ? 'hidden' : 'flex'}`} aria-label="Ask AI about this restaurant" data-id="element-253">
        <Sparkles className="w-6 h-6" data-id="element-254" />
        <span className="font-semibold pr-1" data-id="element-255">Ask AI</span>
      </motion.button>

      <AskAI restaurant={restaurant} isOpen={aiOpen} onClose={() => setAiOpen(false)} data-id="element-256" />
    </motion.div>;
}